import { useEffect, useState } from "react";
import { api, type LibraryCategory, type Project, type Tag } from "../api/client";
import { estimatedStudySpend, profileForStudy } from "../data/studyFixtures";

const STUDY_COLORS = ["#5e8d6f", "#a87c4a", "#3a6688", "#5b3a8c", "#6b6557", "#b04a3a"];

export default function SettingsPage() {
  return (
    <>
      <div className="main-header">
        <h2>Settings</h2>
      </div>
      <div className="main-body">
        <div className="settings-section">
          <h3>Studies</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            Rename, recolor, or archive study workspaces. Archived studies stay searchable
            and can be restored later.
          </p>
          <StudiesEditor />
        </div>

        <div className="settings-section">
          <h3>Library categories</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            Categories tag every source in the library and drive retrieval grouping.
            Rename to update the display label; archive to hide from new uploads
            (existing sources keep their assignment). New categories are available
            immediately on the upload form.
          </p>
          <CategoriesEditor />
        </div>

        <div className="settings-section">
          <h3>Tags</h3>
          <p className="muted" style={{ marginTop: 0 }}>
            Tags are cross-cutting labels for your chats, task runs, and research-agent
            queries. Renaming a tag updates it everywhere it's applied. Deleting a tag
            removes it from all items but keeps the items themselves.
          </p>
          <TagsEditor />
        </div>
      </div>
    </>
  );
}

function StudiesEditor() {
  const [items, setItems] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");

  function refresh() {
    setLoading(true);
    api
      .listProjects(true)
      .then(setItems)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    refresh();
  }, []);

  async function create() {
    const name = newName.trim();
    if (!name) return;
    try {
      await api.createProject(name, STUDY_COLORS[items.length % STUDY_COLORS.length]);
      setNewName("");
      refresh();
    } catch (e) {
      alert(`Couldn't create study: ${(e as Error).message}`);
    }
  }

  async function rename(id: number) {
    const name = editName.trim();
    if (!name) {
      setEditingId(null);
      return;
    }
    try {
      await api.updateProject(id, { name });
      setEditingId(null);
      refresh();
    } catch (e) {
      alert(`Couldn't rename study: ${(e as Error).message}`);
    }
  }

  async function recolor(id: number, color: string) {
    try {
      await api.updateProject(id, { color });
      refresh();
    } catch (e) {
      alert(`Couldn't update study color: ${(e as Error).message}`);
    }
  }

  async function toggleArchive(p: Project) {
    try {
      await api.updateProject(p.id, { archived: !p.archived });
      refresh();
    } catch (e) {
      alert(`Couldn't update study: ${(e as Error).message}`);
    }
  }

  if (loading) return <p className="muted">Loading…</p>;
  if (error) return <p className="error">{error}</p>;

  return (
    <>
      {items.length > 0 && (
        <table className="settings-table studies-settings-table">
          <thead>
            <tr>
              <th>Color</th>
              <th>Name</th>
              <th>Research question</th>
              <th style={{ textAlign: "right" }}>Items</th>
              <th style={{ textAlign: "right" }}>Spend</th>
              <th>Status</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.id} className={p.archived ? "archived" : ""}>
                <td>
                  <div className="swatch-pick">
                    {STUDY_COLORS.map((color) => (
                      <button
                        key={color}
                        type="button"
                        className={(p.color || "") === color ? "active" : ""}
                        style={{ background: color }}
                        aria-label={`Use ${color} for ${p.name}`}
                        onClick={() => recolor(p.id, color)}
                      />
                    ))}
                  </div>
                </td>
                <td>
                  {editingId === p.id ? (
                    <input
                      autoFocus
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") rename(p.id);
                        else if (e.key === "Escape") setEditingId(null);
                      }}
                      onBlur={() => rename(p.id)}
                    />
                  ) : (
                    <span
                      onClick={() => {
                        setEditingId(p.id);
                        setEditName(p.name);
                      }}
                      style={{ cursor: "pointer" }}
                      title="Click to rename"
                    >
                      {p.name}
                    </span>
                  )}
                </td>
                <td>
                  <span className="study-question-cell">{profileForStudy(p).question}</span>
                </td>
                <td style={{ textAlign: "right" }}>{p.counts.total}</td>
                <td style={{ textAlign: "right" }} className="mono">
                  ${estimatedStudySpend(p).toFixed(2)}
                </td>
                <td>{p.archived ? <span className="muted">archived</span> : "active"}</td>
                <td style={{ textAlign: "right" }}>
                  <button className="ghost-link" onClick={() => toggleArchive(p)}>
                    {p.archived ? "Restore" : "Archive"}
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="row" style={{ marginTop: 12 }}>
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && create()}
          placeholder="New study name"
          style={{ flex: 1, maxWidth: 360 }}
        />
        <button className="primary" onClick={create} disabled={!newName.trim()}>
          Add
        </button>
      </div>
    </>
  );
}

function CategoriesEditor() {
  const [items, setItems] = useState<LibraryCategory[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newLabel, setNewLabel] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editLabel, setEditLabel] = useState("");

  function refresh() {
    setLoading(true);
    api
      .listLibraryCategories(true)
      .then(setItems)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    refresh();
  }, []);

  async function create() {
    const label = newLabel.trim();
    if (!label) return;
    try {
      await api.createLibraryCategory(label);
      setNewLabel("");
      refresh();
    } catch (e) {
      alert(`Couldn't create: ${(e as Error).message}`);
    }
  }

  async function rename(id: number) {
    const label = editLabel.trim();
    if (!label) {
      setEditingId(null);
      return;
    }
    try {
      await api.updateLibraryCategory(id, { label });
      setEditingId(null);
      refresh();
    } catch (e) {
      alert(`Couldn't rename: ${(e as Error).message}`);
    }
  }

  async function toggleArchive(c: LibraryCategory) {
    try {
      await api.updateLibraryCategory(c.id, { archived: !c.archived });
      refresh();
    } catch (e) {
      alert(`Couldn't update: ${(e as Error).message}`);
    }
  }

  async function remove(c: LibraryCategory) {
    if (c.source_count > 0) {
      alert(
        `Can't delete "${c.label}" — ${c.source_count} source${
          c.source_count === 1 ? "" : "s"
        } still use it. Archive it instead, or move sources first.`,
      );
      return;
    }
    if (!window.confirm(`Delete category "${c.label}"?`)) return;
    try {
      await api.deleteLibraryCategory(c.id);
      refresh();
    } catch (e) {
      alert(`Couldn't delete: ${(e as Error).message}`);
    }
  }

  if (loading) return <p className="muted">Loading…</p>;
  if (error) return <p className="error">{error}</p>;

  return (
    <>
      <table className="settings-table">
        <thead>
          <tr>
            <th>Label</th>
            <th>Key</th>
            <th style={{ textAlign: "right" }}>Sources</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {items.map((c) => (
            <tr key={c.id} className={c.archived ? "archived" : ""}>
              <td>
                {editingId === c.id ? (
                  <input
                    autoFocus
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") rename(c.id);
                      else if (e.key === "Escape") setEditingId(null);
                    }}
                    onBlur={() => rename(c.id)}
                  />
                ) : (
                  <span
                    onClick={() => {
                      setEditingId(c.id);
                      setEditLabel(c.label);
                    }}
                    style={{ cursor: "pointer" }}
                    title="Click to rename"
                  >
                    {c.label}
                  </span>
                )}
              </td>
              <td className="mono muted">{c.key}</td>
              <td style={{ textAlign: "right" }}>{c.source_count}</td>
              <td>{c.archived ? <span className="muted">archived</span> : "active"}</td>
              <td style={{ textAlign: "right" }}>
                <button className="ghost-link" onClick={() => toggleArchive(c)}>
                  {c.archived ? "Unarchive" : "Archive"}
                </button>
                <button
                  className="danger-link"
                  onClick={() => remove(c)}
                  disabled={c.source_count > 0}
                  title={c.source_count > 0 ? "Has sources — archive instead" : "Delete"}
                >
                  Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="row" style={{ marginTop: 12 }}>
        <input
          value={newLabel}
          onChange={(e) => setNewLabel(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && create()}
          placeholder="New category (e.g. Ethnographic methods)"
          style={{ flex: 1, maxWidth: 360 }}
        />
        <button className="primary" onClick={create} disabled={!newLabel.trim()}>
          Add
        </button>
      </div>
    </>
  );
}

const TAG_COLORS = ["#7a4f1c", "#2f6f4f", "#1d4e89", "#5b3a8c", "#b04a3a", "#6b6557", "#a07a14"];

function TagsEditor() {
  const [items, setItems] = useState<Tag[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [newName, setNewName] = useState("");
  const [editingId, setEditingId] = useState<number | null>(null);
  const [editName, setEditName] = useState("");

  function refresh() {
    setLoading(true);
    api
      .listTags()
      .then(setItems)
      .catch((e) => setError(String(e)))
      .finally(() => setLoading(false));
  }

  useEffect(() => {
    refresh();
  }, []);

  async function create() {
    const name = newName.trim();
    if (!name) return;
    try {
      const color = TAG_COLORS[items.length % TAG_COLORS.length];
      await api.createTag(name, color);
      setNewName("");
      refresh();
    } catch (e) {
      alert(`Couldn't create: ${(e as Error).message}`);
    }
  }

  async function rename(id: number) {
    const name = editName.trim();
    if (!name) {
      setEditingId(null);
      return;
    }
    try {
      await api.updateTag(id, { name });
      setEditingId(null);
      refresh();
    } catch (e) {
      alert(`Couldn't rename: ${(e as Error).message}`);
    }
  }

  async function recolor(id: number, color: string) {
    try {
      await api.updateTag(id, { color });
      refresh();
    } catch (e) {
      alert(`Couldn't update color: ${(e as Error).message}`);
    }
  }

  async function remove(t: Tag) {
    const msg =
      t.item_count > 0
        ? `Delete tag "${t.name}"? It's currently applied to ${t.item_count} item${
            t.item_count === 1 ? "" : "s"
          } — they'll lose this tag but stay otherwise unchanged.`
        : `Delete tag "${t.name}"?`;
    if (!window.confirm(msg)) return;
    try {
      await api.deleteTag(t.id);
      refresh();
    } catch (e) {
      alert(`Couldn't delete: ${(e as Error).message}`);
    }
  }

  if (loading) return <p className="muted">Loading…</p>;
  if (error) return <p className="error">{error}</p>;

  return (
    <>
      {items.length === 0 ? (
        <p className="muted">
          No tags yet. Add tags inline from the History page (each card has a "+
          tag" button), or create them here.
        </p>
      ) : (
        <table className="settings-table">
          <thead>
            <tr>
              <th>Color</th>
              <th>Name</th>
              <th style={{ textAlign: "right" }}>Used</th>
              <th />
            </tr>
          </thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id}>
                <td>
                  <input
                    type="color"
                    value={t.color || "#888888"}
                    onChange={(e) => recolor(t.id, e.target.value)}
                    style={{ width: 28, height: 22, padding: 0, border: "none", background: "transparent", cursor: "pointer" }}
                  />
                </td>
                <td>
                  {editingId === t.id ? (
                    <input
                      autoFocus
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") rename(t.id);
                        else if (e.key === "Escape") setEditingId(null);
                      }}
                      onBlur={() => rename(t.id)}
                    />
                  ) : (
                    <span
                      onClick={() => {
                        setEditingId(t.id);
                        setEditName(t.name);
                      }}
                      style={{ cursor: "pointer" }}
                      title="Click to rename"
                    >
                      {t.name}
                    </span>
                  )}
                </td>
                <td style={{ textAlign: "right" }}>{t.item_count}</td>
                <td style={{ textAlign: "right" }}>
                  <button className="danger-link" onClick={() => remove(t)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <div className="row" style={{ marginTop: 12 }}>
        <input
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && create()}
          placeholder="New tag name"
          style={{ flex: 1, maxWidth: 280 }}
        />
        <button className="primary" onClick={create} disabled={!newName.trim()}>
          Add
        </button>
      </div>
    </>
  );
}
